// client/src/components/users/UserList.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import userService from '../../services/userService';

const UserList = () => {
  const { t, i18n } = useTranslation();
  const { user, isAdmin } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Current language
  const lang = i18n.language;

  // Function to load users
  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getUsers();
      setUsers(response.data);
      setError('');
    } catch (err) {
      setError(t('errorLoadingUsers'));
    } finally {
      setLoading(false);
    }
  };

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  // Handle user deletion
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await userService.deleteUser(userToDelete.id);
      setSuccess(t('userDeletedSuccess'));
      loadUsers();
    } catch (err) {
      setError(t('errorDeletingUser'));
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleCloseAlert = () => {
    setSuccess('');
    setError('');
  };

  // Get role display text
  const getRoleDisplay = (role) => {
    return role === 'admin' ? t('admin') : t('facultyUser');
  };
  
  // Get user type chip color
  const getUserTypeColor = (userType) => {
    switch (userType) {
      case 'maker':
        return 'info';
      case 'checker':
        return 'warning';
      case 'both':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('users')}
      </Typography>

      {/* Alert messages */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Add User button - only for admins */}
      {isAdmin() && (
        <Box sx={{ mb: 2, textAlign: direction === 'ltr' ? 'right' : 'left' }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/users/create"
          >
            {t('addUser')}
          </Button>
        </Box>
      )}

      {/* Users Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : users.length === 0 ? (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1">
            {t('noUsersFound')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('username')}</TableCell>
                <TableCell>{t('email')}</TableCell>
                <TableCell>{t('role')}</TableCell>
                <TableCell>{t('userType')}</TableCell>
                <TableCell>{t('faculty')}</TableCell>
                <TableCell align="center">{t('status')}</TableCell>
                <TableCell align="center">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((userData) => (
                <TableRow key={userData.id}>
                  <TableCell>{userData.username}</TableCell>
                  <TableCell>{userData.email}</TableCell>
                  <TableCell>
                    <Chip 
                      label={getRoleDisplay(userData.role)} 
                      color={userData.role === 'admin' ? 'primary' : 'secondary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {userData.role === 'faculty' && (
                      <Tooltip title={t(`${userData.user_type}Description`)}>
                        <Chip 
                          label={t(userData.user_type)} 
                          color={getUserTypeColor(userData.user_type)}
                          size="small"
                          icon={<InfoIcon sx={{ fontSize: '0.9rem' }} />}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    {userData.faculty 
                      ? userData.faculty[`name_${lang}`] 
                      : t('noFaculty')}
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={userData.is_active ? t('active') : t('inactive')} 
                      color={userData.is_active ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {/* Anyone can edit their own profile */}
                    {(isAdmin() || userData.id === user.id) && (
                      <IconButton
                        color="primary"
                        component={RouterLink}
                        to={`/users/edit/${userData.id}`}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {/* Only admins can delete users, and can't delete themselves */}
                    {isAdmin() && userData.id !== user.id && (
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(userData)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>{t('confirmDelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deleteUserConfirmation', { 
              username: userToDelete?.username 
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserList;